import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { Description, Search, Folder, ArrowBack } from '@mui/icons-material';
import { FileMetadata } from '../../types';
import { apiService } from '../../services/api';
import { DocumentComments } from '../ProjectExecution/DocumentComments';

/** Matches BACKEND auditExportService / changeOrderAuditService: Project Audit Logs */
function isProjectAuditLogsFolderName(name: string): boolean {
  return name.trim().toLowerCase() === 'project audit logs';
}

interface LibraryBrowserProps {
  onFileSelect: (file: FileMetadata) => void;
  projectId?: number; // Optional - project scope when available
  folderPath?: string; // Project-relative path
  onFolderPathChange?: (folderPath: string | undefined) => void;
  /** Tenant/Client: hide SharePoint comments panel and audit export folder */
  externalDocumentsMode?: boolean;
  // Legacy props (deprecated - kept for backward compatibility)
  siteId?: string;
  driveId?: string;
  libraryName?: string;
}

const LibraryBrowser = ({ 
  onFileSelect, 
  projectId, // Required for project-scoped access
  folderPath,
  onFolderPathChange,
  externalDocumentsMode = false,
  // Legacy props (for backward compatibility)
  siteId,
  driveId,
  libraryName,
}: LibraryBrowserProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<FileMetadata[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<FileMetadata | null>(null);
  // Current path is project-relative (e.g., ['Subfolder1', 'Subfolder2'])
  const [currentPath, setCurrentPath] = useState<string[]>(
    folderPath ? folderPath.split('/').filter(p => p) : []
  );
  
  // Determine if using new project-scoped API or legacy API
  const useProjectScopedApi = projectId !== undefined && projectId !== null;

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (useProjectScopedApi) {
          // New project-scoped API: 
          // Project root: /api/sp/list?projectId={projectId} (no folderPath)
          // Subfolder: /api/sp/list?projectId={projectId}&folderPath={path}
          const pathString = currentPath.length > 0 ? currentPath.join('/') : undefined;
          const response = await apiService.listProjectSharePointFiles(
            projectId,
            pathString
          );
          setItems(response.items);
        } else {
          // Legacy API (for backward compatibility)
          if (!siteId) {
            setError('Site ID is required to load files');
            setLoading(false);
            return;
          }

          if (!driveId && !libraryName) {
            setError('Either Drive ID or Library Name is required to load files');
            setLoading(false);
            return;
          }

          // Build folder path from current path array
          const pathString = currentPath.length > 0 ? currentPath.join('/') : undefined;
          const response = await apiService.listSharePointFiles(
            siteId, 
            driveId, 
            libraryName, 
            pathString
          );
          setItems(response.items);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load files';
        // Check for 403 Forbidden (out of scope)
        if (errorMessage.includes('403') || errorMessage.includes('Forbidden') || errorMessage.includes('outside project scope')) {
          setError('Access denied: Path is outside project scope');
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, siteId, driveId, libraryName, currentPath.join('/'), useProjectScopedApi]);

  // Update current path when folderPath prop changes
  useEffect(() => {
    if (folderPath !== undefined) {
      setCurrentPath(folderPath ? folderPath.split('/') : []);
    }
  }, [folderPath]);

  const filteredItems = items
    .filter(
      (item) =>
        !externalDocumentsMode ||
        !item.isFolder ||
        !isProjectAuditLogsFolderName(item.name)
    )
    .filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const folders = filteredItems.filter(item => item.isFolder);
  const files = filteredItems.filter(item => item.isFile);

  const handleItemClick = (item: FileMetadata) => {
    if (item.isFolder && externalDocumentsMode && isProjectAuditLogsFolderName(item.name)) {
      return;
    }
    if (item.isFolder) {
      // Navigate into folder (only within project scope)
      const newPath = [...currentPath, item.name];
      setCurrentPath(newPath);
      if (onFolderPathChange) {
        onFolderPathChange(newPath.join('/'));
      }
      // Clear selected document when navigating to folder
      setSelectedDocument(null);
    } else {
      // Select file - set as selected for comments display
      setSelectedDocument(item);
      // Also call the callback for file opening
      onFileSelect(item);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    // Only allow navigation within project scope (can't go above project root)
    // index 0 is project root, so we can navigate to any index >= 0
    const newPath = currentPath.slice(0, index);
    setCurrentPath(newPath);
    if (onFolderPathChange) {
      onFolderPathChange(newPath.length > 0 ? newPath.join('/') : undefined);
    }
  };

  const handleBackClick = () => {
    // Only allow going back within project scope (can't go above project root)
    if (currentPath.length > 0) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      if (onFolderPathChange) {
        onFolderPathChange(newPath.length > 0 ? newPath.join('/') : undefined);
      }
    }
  };
  
  // Get project-relative path display (for breadcrumbs)
  // const getProjectRelativePath = () => {
  //   if (currentPath.length === 0) {
  //     return 'Project Root';
  //   }
  //   return currentPath.join(' / ');
  // };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        SharePoint Library Browser
      </Typography>

      {/* Breadcrumbs - Show project-relative path */}
      {useProjectScopedApi ? (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setCurrentPath([]);
              if (onFolderPathChange) {
                onFolderPathChange(undefined);
              }
            }}
            sx={{ cursor: 'pointer', fontWeight: currentPath.length === 0 ? 'bold' : 'normal' }}
          >
            Project Root
          </Link>
          {currentPath.map((segment, index) => (
            <Link
              key={index}
              component="button"
              variant="body2"
              onClick={() => handleBreadcrumbClick(index + 1)}
              sx={{ cursor: 'pointer' }}
            >
              {segment}
            </Link>
          ))}
        </Breadcrumbs>
      ) : (
        // Legacy breadcrumbs (for backward compatibility)
        (currentPath.length > 0 || libraryName) && (
          <Breadcrumbs sx={{ mb: 2 }}>
            {libraryName && (
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setCurrentPath([]);
                  if (onFolderPathChange) {
                    onFolderPathChange(undefined);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              >
                {libraryName}
              </Link>
            )}
            {currentPath.map((segment, index) => (
              <Link
                key={index}
                component="button"
                variant="body2"
                onClick={() => handleBreadcrumbClick(index + 1)}
                sx={{ cursor: 'pointer' }}
              >
                {segment}
              </Link>
            ))}
          </Breadcrumbs>
        )
      )}

      {/* Back button - Only show if not at project root */}
      {currentPath.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <ListItemButton onClick={handleBackClick} sx={{ pl: 0 }}>
            <ListItemIcon>
              <ArrowBack />
            </ListItemIcon>
            <ListItemText primary="Back" />
          </ListItemButton>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search files and folders..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <List>
          {filteredItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No items found
            </Typography>
          ) : (
            <>
              {/* Show folders first */}
              {folders.map((folder) => (
                <ListItem key={folder.itemId} disablePadding>
                  <ListItemButton onClick={() => handleItemClick(folder)}>
                    <ListItemIcon>
                      <Folder color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={folder.name}
                      secondary="Folder"
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {/* Then show files */}
              {files.map((file) => (
                <ListItem 
                  key={file.itemId} 
                  disablePadding
                  selected={selectedDocument?.itemId === file.itemId}
                >
                  <ListItemButton onClick={() => handleItemClick(file)}>
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary={file.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          )}
        </List>
      )}

      {/* Document Comments Panel - internal users only */}
      {!externalDocumentsMode && selectedDocument && selectedDocument.isFile && (
        <Box sx={{ mt: 2 }}>
          <DocumentComments
            file={{
              spItemId: selectedDocument.spItemId ?? null,
              siteId: selectedDocument.siteId,
              driveId: selectedDocument.driveId,
              itemId: selectedDocument.itemId,
            }}
          />
        </Box>
      )}

    </Paper>
  );
};

export default LibraryBrowser;

